<template>
  <div v-if="selectedSpot?.id">
    <v-card flat>
      <v-card-title class="d-flex flex-row card-header">
        <h3 class="text-uppercase" data-testid="yard-spot-detail-title">
          {{ selectedSpot.code }}
        </h3>
        <v-spacer></v-spacer>
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          @click="closeDetail"
          data-testid="yard-spot-detail-close-button"></v-btn>
      </v-card-title>
      <v-card-text
        data-testid="yard-spot-detail-content"
        :class="
          isSpotAssigned(selectedSpot) && !hasAssetVisitOnSpot(selectedSpot)
            ? 'card-content-no-actions'
            : 'card-content'
        ">
        <h2 class="text-capitalize" v-if="!isSpotAssigned(selectedSpot)">
          {{ t(`core.yard.spotStatuses.${camelCase(getSpotStatus(selectedSpot))}`) }}
        </h2>

        <div>
          <yard-view-spot-detail-open v-if="isSpotOpen(selectedSpot)" />
          <yard-view-spot-detail-closed v-if="isSpotClosed(selectedSpot)" />
          <yard-view-spot-detail-assigned
            :value="activeTab"
            @update="value => (activeTab = value)"
            :spot-assignment="spotAssignment"
            v-if="isSpotAssigned(selectedSpot)" />
        </div>
      </v-card-text>

      <v-card-actions class="card-actions" data-testid="yard-spot-detail-actions">
        <div class="d-flex flex-column full-width">
          <v-btn block class="bg-primary" @click="openSpot" v-if="isSpotClosed(selectedSpot)">
            <v-icon size="small" class="mr-2">mdi-check</v-icon>
            {{ t('yard.pages.yardView.buttons.openThisSpot') }}
          </v-btn>
          <v-btn block variant="outlined" @click="closeSpot" v-if="isSpotOpen(selectedSpot)">
            <v-icon size="small" class="mr-2">mdi-close</v-icon>
            {{ t('yard.pages.yardView.buttons.closeThisSpot') }}
          </v-btn>

          <template v-if="isSpotAssigned(selectedSpot)">
            <yard-view-drop-hook-button
              :spot="selectedSpot"
              v-if="
                hasAssetVisitOnSpot(selectedSpot) &&
                selectedSpot.spotAssignments.every(
                  assignment => assignment.assetVisit?.visitType !== AssetVisitType.Live
                )
              "
              @open-detach-dialog="dialogAssetVisitDetachOpen = true"
              @open-attach-dialog="dialogAssetVisitAttachOpen = true" />

            <v-btn
              block
              @click="updateSpotAssignmentType = 'depart'"
              class="mb-2"
              variant="outlined">
              <v-icon size="small" class="mr-1">mdi-truck-fast-outline</v-icon>
              {{ t('yard.pages.yardView.buttons.departed') }}
            </v-btn>
            <v-btn block variant="flat" @click="updateSpotAssignmentType = 'unassign'">
              {{ t('yard.pages.yardView.buttons.unassign') }}
            </v-btn>
          </template>
        </div>
      </v-card-actions>
    </v-card>
    <yard-view-asset-visit-attach-dialog
      v-if="hasAssetContainerOnSpot(selectedSpot)"
      :spot="selectedSpot"
      :is-visible="dialogAssetVisitAttachOpen"
      @close="dialogAssetVisitAttachOpen = false" />
    <yard-view-asset-visit-detach-dialog
      :spot="selectedSpot"
      :is-visible="dialogAssetVisitDetachOpen"
      @close="dialogAssetVisitDetachOpen = false" />
    <yard-view-spot-status-dialog :status-type="dialogStatusOpen" :spot="selectedSpot" />
    <yard-view-spot-assignment-update-dialog
      v-if="isSpotAssigned(selectedSpot)"
      :update-type="updateSpotAssignmentType"
      :asset-type="activeTab"
      :spot-assignment="spotAssignment" />
  </div>
</template>

<script>
import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue';
import { useEventHub, useRouter } from '@/composables';

import useYard from '@/modules/yard/composables/useYard';
import YardViewSpotStatusDialog from '@/modules/yard/components/Spot/Dialogs/YardViewSpotStatusDialog.vue';
import YardViewSpotDetailOpen from '@/modules/yard/components/Spot/Detail/YardViewSpotDetailOpen.vue';
import YardViewSpotDetailClosed from '@/modules/yard/components/Spot/Detail/YardViewSpotDetailClosed.vue';
import YardViewSpotDetailAssigned from '@/modules/yard/components/Spot/Detail/YardViewSpotDetailAssigned.vue';
import { AssetVisitType, DepartTypeEnum } from '@nova/core';
import { camelCase } from 'lodash';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewSpotDetail',
  methods: { camelCase },
  components: {
    YardViewSpotDetailAssigned,
    YardViewSpotDetailClosed,
    YardViewSpotDetailOpen,
    YardViewSpotStatusDialog
  },
  setup() {
    const { t } = useI18n();
    const yardStore = useYard();
    const router = useRouter();
    const eventHub = useEventHub();
    const spotAssignment = computed(() => yardStore.selectedSpotRef.value.spotAssignments[0]);
    const dialogStatusOpen = ref(null);
    const dialogAssetVisitDetachOpen = ref(false);
    const dialogAssetVisitAttachOpen = ref(false);
    const updateSpotAssignmentType = ref(null);
    const activeTab = ref(DepartTypeEnum.AssetVisit);

    const closeSpot = () => {
      dialogStatusOpen.value = 'close';
    };

    const openSpot = () => {
      dialogStatusOpen.value = 'open';
    };

    const closeDetail = () => {
      yardStore.selectedSpotRef.value = null;
      // clean up the URL
      router.push({
        name: 'yard-warehouse',
        params: { warehouseId: yardStore.selectedWarehouseRef.value.id }
      });
    };

    const getSpotStatus = spot => {
      return yardStore.getSpotStatus(spot);
    };

    onMounted(() => {
      eventHub.$on('close-spot-status-dialog', () => {
        dialogStatusOpen.value = null;
      });

      eventHub.$on('close-spot-assignment-update-dialog', () => {
        updateSpotAssignmentType.value = null;
      });

      if (yardStore.hasAssetContainerOnlyOnSpot(yardStore.selectedSpotRef.value)) {
        activeTab.value = DepartTypeEnum.AssetContainer;
      } else {
        activeTab.value = DepartTypeEnum.AssetVisit;
      }
    });

    onBeforeUnmount(() => {
      eventHub.$off('close-spot-status-dialog');
      eventHub.$off('close-spot-assignment-update-dialog');
    });

    return {
      t,
      selectedWarehouse: yardStore.selectedWarehouseRef,
      openSpot,
      closeSpot,
      closeDetail,
      getSpotStatus,
      dialogAssetVisitAttachOpen,
      dialogAssetVisitDetachOpen,
      updateSpotAssignmentType,
      isSpotAssigned: yardStore.isSpotAssigned,
      isSpotClosed: yardStore.isSpotClosed,
      isSpotOpen: yardStore.isSpotOpen,
      hasAssetVisitOnSpot: yardStore.hasAssetVisitOnSpot,
      hasAssetContainerOnSpot: yardStore.hasAssetContainerOnSpot,
      spotAssignment,
      dialogStatusOpen,
      selectedSpot: yardStore.selectedSpotRef,
      spotStatus: yardStore.spotStatus,
      activeTab,
      AssetVisitType
    };
  }
});
</script>
